import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { DateTime } from 'luxon';
import { FC, MouseEvent, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { ReactComponent as HyperlinkIcon } from '@/assets/icons/hyperlink.svg';
// import { ReactComponent as PencilIcon } from '@/assets/icons/pencil.svg';
import { ReactComponent as PrioritizeIcon } from '@/assets/icons/prioritize.svg';
import { ReactComponent as TrashIcon } from '@/assets/icons/trashbin.svg';
import ActionsDropdown, {
  ActionsDropdownButton,
} from '@/components/ActionsDropdown';
import { ActionsWrap, Td, Thumbnail } from '@/components/Table';
import Text from '@/components/Text';
import useNetwork from '@/hooks/dq/useNetwork';
import useAlerts from '@/hooks/useAlerts';
import useTableColumnViewOption from '@/hooks/useTableColumnViewOption';
import useToasts from '@/hooks/useToasts';
import {
  EditorialFacebookPostResource,
  EditorialQueuePage,
} from '@/types/resources';
import { api } from '@/utils/api';
import { getNetworkLabel } from '@/utils/dq';
import {
  EditorialFacebookPostNetwork,
  EditorialFacebookPostType,
} from '@/utils/enums';
import { Color } from '@/utils/theme';

import PostForm from './PostForm';
import PostType from './PostType';
import {
  Checkbox,
  CheckboxHolder,
  CheckboxLabel,
  CheckboxWrap,
  tableColumns,
} from './Table';

const ThumbnailTd = styled(Td)`
  ${Thumbnail} {
    max-width: 89px;
  }
`;

const Indicator = styled.div`
  display: flex;
  align-items: center;
`;

const Dot = styled.div<{ $color: Color }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  margin-right: 8px;
  background: ${({ $color, theme }) => theme.colors[$color]};
`;

const ChannelHolder = styled.div`
  display: flex;
  align-items: center;
`;

const ChannelImage = styled.img`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 6px;
  flex-shrink: 0;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
`;

const ChannelName = styled.span`
  white-space: nowrap;
`;

const Time = styled(Text)`
  margin-top: 4px;
`;

export const LinkTd = styled(Td)`
  white-space: nowrap;
  max-width: 200px;
  overflow: hidden;
`;

export const HyperLink = styled('a').attrs({
  rel: 'noreferrer noopener',
  target: '_blank',
})`
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  svg {
    margin-right: 6px;
    width: 14px;
    flex-shrink: 0;
  }
  > span {
    overflow-x: hidden;
    text-overflow: ellipsis;
  }
`;

const PlaceholderTd = styled.td`
  background: ${({ theme }) => theme.colors.grayscaleExtraLight} !important;
  position: relative !important;
  padding-left: 0 !important;
  padding-right: 0 !important;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grayscale};
  vertical-align: middle !important;
  ${Text} {
    position: sticky;
    left: 0;
    top: 0;
    height: 100%;
    display: flex;
    align-items: center;
    width: 1120px;
    justify-content: center;
  }
`;

const NetworkLabel = styled.span`
  text-transform: capitalize;
`;

const BackgroundIndicator = styled(Text)<{ $applied: boolean }>`
  margin-top: 8px;
  padding: 4px 6px;
  border-radius: 4px;
  background: ${({ theme, $applied }) =>
    $applied ? '#4136CF14' : theme.colors.grayscaleLight};
  color: ${({ theme, $applied }) =>
    $applied ? theme.colors.purple : theme.colors.gray};
  display: inline-block;
`;

const OrderNumber = styled.span`
  margin-right: 22px;
`;

interface Props {
  post: EditorialFacebookPostResource;
  order: number;
  postDate: DateTime | null;
  activePage: EditorialQueuePage;
  toggleSelection: (id: number) => void;
  isChecked: boolean;
}

const TableRow: FC<Props> = ({
  post,
  order,
  postDate,
  activePage,
  isChecked,
  toggleSelection,
}) => {
  const [showEditForm, setShowEditForm] = useState(false);

  const { pageId, status } = useParams();

  // eslint-disable-next-line
  const [_, networkLabel] = useNetwork();

  const alerts = useAlerts();
  const toasts = useToasts();

  const queryClient = useQueryClient();

  const tableViewOptionsKey = `${status!}_${networkLabel}_dq_posts_table`;

  const getColumnVisibility = useTableColumnViewOption(
    tableViewOptionsKey,
    tableColumns,
  );

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirm = await alerts.confirm(
        `Are you sure you want to delete this post?`,
      );

      if (!confirm) return;

      await api.delete<never, { id: number }>(
        `/v1/editorial/facebook-posts/${post.id}`,
      );

      await queryClient.invalidateQueries(
        ['editorial_distribution_schedules'],
        {
          exact: false,
        },
      );

      alerts.success('Post was successfully deleted');
    } catch (e) {
      alerts.error();
    }
  };

  const onPrioritize = async (e: MouseEvent<HTMLButtonElement>) => {
    const toastId = toasts.create('loading', 'Loading...');

    try {
      await api.patch(`/v1/editorial/facebook-posts/${post.id}/prioritize`);

      await queryClient.invalidateQueries([
        'editorial_distribution_schedules',
        {
          queueable: true,
          status: 'queued',
          selectedChannels: pageId,
        },
      ]);

      toasts.success(toastId, 'Saved');
    } catch (e) {
      toasts.error(toastId, 'Error!');
    }
  };

  const renderStatus = () => {
    if (post.queueStatus === 1) {
      if (post.queueMeta?.is_processing) {
        return (
          <Indicator>
            <Dot $color={'warning'} />
            Processing
          </Indicator>
        );
      }
      return (
        <Indicator>
          <Dot $color={'warning'} />
          In Queue
        </Indicator>
      );
    }

    return (
      <Indicator>
        <Dot $color={'success'} />
        Posted
      </Indicator>
    );
  };

  const postHasLinkStats = [
    EditorialFacebookPostType.LINK,
    EditorialFacebookPostType.IMAGE,
  ].includes(post.type);

  if (post.type === EditorialFacebookPostType.PLACEHOLDER) {
    return (
      <tr>
        <PlaceholderTd colSpan={12}>
          <Text $color="gray" $type="smallBold">
            Time gap placeholder
            {!!postDate && ` · ${postDate.toFormat('HH:mm')}`}
          </Text>
        </PlaceholderTd>
        <Td $actionsCol>
          <ActionsWrap>
            <ActionsDropdown>
              <ActionsDropdownButton as="button" onClick={onPrioritize}>
                <span>
                  <PrioritizeIcon />
                </span>
                Prioritise
              </ActionsDropdownButton>
              <ActionsDropdownButton as="button" onClick={onDelete}>
                <span>
                  <TrashIcon />
                </span>
                Delete
              </ActionsDropdownButton>
            </ActionsDropdown>
          </ActionsWrap>
        </Td>
      </tr>
    );
  }

  return (
    <tr>
      <Td>
        <CheckboxWrap>
          <CheckboxHolder>
            <Checkbox
              id={`checkbox-post-${post.id}`}
              name="products"
              type="checkbox"
              checked={isChecked}
              onChange={() => toggleSelection(post.id)}
            />
            <CheckboxLabel htmlFor={`checkbox-post-${post.id}`} />
          </CheckboxHolder>
          <OrderNumber>{order}</OrderNumber>
        </CheckboxWrap>
      </Td>
      {getColumnVisibility('Cover') && (
        <ThumbnailTd>
          <Thumbnail
            src={post.imageUrl}
            alt={'Cover'}
            type={
              post.type === EditorialFacebookPostType.REEL ? 'reel' : 'image'
            }
          />
        </ThumbnailTd>
      )}
      {getColumnVisibility('Message') && (
        <Td>
          <div>{post.message ?? '-'}</div>
          {post.type === EditorialFacebookPostType.TEXT &&
            post.network === EditorialFacebookPostNetwork.FACEBOOK && (
              <BackgroundIndicator
                $type="extraSmall"
                $applied={post.queueMeta?.background_applied ?? false}
              >
                {post.queueMeta?.background_applied
                  ? 'Background Applied'
                  : 'Background Unapplied'}
              </BackgroundIndicator>
            )}
        </Td>
      )}
      {getColumnVisibility('Network') && (
        <Td>
          <NetworkLabel>{getNetworkLabel(post.network)}</NetworkLabel>
        </Td>
      )}
      {getColumnVisibility('Page Name') && (
        <Td>
          <ChannelHolder>
            {!!post.channel.thumbnail && (
              <ChannelImage
                src={post.channel.thumbnail}
                alt={post.channel.name + ' thumbnail'}
              />
            )}
            <ChannelName>{post.channel.name}</ChannelName>
          </ChannelHolder>
        </Td>
      )}
      {getColumnVisibility('Content Type') && (
        <Td>
          <PostType type={post.type} />
        </Td>
      )}
      {getColumnVisibility('Estimated Post Date') && (
        <Td>
          {postDate ? (
            <>
              {postDate.toLocaleString()} <br />
              <Time $color="gray" $type="extraSmall">
                {postDate.toFormat('HH:mm')}
              </Time>
            </>
          ) : (
            '-'
          )}
        </Td>
      )}
      {getColumnVisibility('Status') && <Td>{renderStatus()}</Td>}
      {getColumnVisibility('Site Visit Benchmark') && (
        <Td>{!postHasLinkStats ? '-' : post.siteVisitBenchmark ?? '-'}</Td>
      )}
      {getColumnVisibility('Reach Duration') && (
        <Td>{!postHasLinkStats ? '-' : post.reachDuration}</Td>
      )}
      {getColumnVisibility('Article Link') && (
        <LinkTd>
          {post.link ? (
            <HyperLink href={post.link}>
              <HyperlinkIcon />
              <span>{post.link}</span>
            </HyperLink>
          ) : (
            '-'
          )}
        </LinkTd>
      )}
      {getColumnVisibility('Owner') && <Td>{post.owner}</Td>}
      {getColumnVisibility('Notes') && <Td>{post.notes ?? '-'}</Td>}
      <Td $actionsCol>
        <ActionsWrap>
          <ActionsDropdown>
            {/* <ActionsDropdownButton
              as="button"
              onClick={() => setShowEditForm(true)}
            >
              <span>
                <PencilIcon />
              </span>
              Edit
            </ActionsDropdownButton> */}
            <ActionsDropdownButton as="button" onClick={onPrioritize}>
              <span>
                <PrioritizeIcon />
              </span>
              Prioritise
            </ActionsDropdownButton>
            <ActionsDropdownButton as="button" onClick={onDelete}>
              <span>
                <TrashIcon />
              </span>
              Delete
            </ActionsDropdownButton>
          </ActionsDropdown>
        </ActionsWrap>
      </Td>
      <AnimatePresence>
        {showEditForm && (
          <PostForm
            post={post}
            onClose={() => setShowEditForm(false)}
            activePage={activePage}
          />
        )}
      </AnimatePresence>
    </tr>
  );
};

export default TableRow;
